import { useState } from 'react'

import { OneDataCollection } from '@factorialco/factorial-one-react/dist/experimental'
import { Delete, Upload } from '@factorialco/factorial-one-react/icons/app'
import {
  ProjectDocumentsDataCollectionDocument,
  ProjectDocumentsDataCollectionItemFragment,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { DeleteDocumentsModal } from './modals/DeleteDocumentsModal'

interface ProjectDocumentsDataCollectionProps {
  projectId: number
  canManageDocuments: boolean
  onClickUpload: () => void
}

const ProjectDocumentsDataCollection = ({
  projectId,
  canManageDocuments,
  onClickUpload,
}: ProjectDocumentsDataCollectionProps) => {
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const handleOnClickDelete = () => {
    track(trackingEvents.projectManagement.projectFiles.clickOnDeleteFile)
    setIsOpenModal(true)
  }

  const handleOnCloseModal = () => {
    setIsOpenModal(false)
  }

  const source = useGraphqlDataSource({
    document: ProjectDocumentsDataCollectionDocument,
    mapData: (data) => {
      return data.projectManagement.projectDocumentsConnection.nodes.filter(
        (item) => !item.deletedAt
      )
    },
    mapVariables: ({ sortings }) => ({
      projectId,
      sortOrder: sortings?.[0],
    }),
    mapPagination: (data) => ({
      total: data.projectManagement.projectDocumentsConnection.totalCount ?? 0,
    }),
    itemOnClick:
      ({ url }) =>
      () => {
        track(trackingEvents.projectManagement.projectFiles.clickOnDownloadFile)
        window.open(buildUrl(url), '_blank')
      },
    itemActions: ({ id }) => {
      return [
        {
          label: i18n.t('common.delete'),
          icon: Delete,
          onClick: () => {
            setSelectedItems([id])
            handleOnClickDelete()
          },
        },
      ]
    },

    primaryActions: () =>
      canManageDocuments
        ? {
            label: i18n.t('project_management.files.datacollection.upload_file'),
            icon: Upload,
            onClick: () => {
              track(trackingEvents.projectManagement.projectFiles.clickOnUploadFile)
              onClickUpload()
            },
          }
        : undefined,
    selectable: (item) => item.id,
    bulkActions: () => {
      return {
        primary: [
          {
            id: 'delete-documents',
            label: i18n.t('common.delete'),
            icon: Delete,
            critical: true,
          },
        ],
      }
    },
    sortings: {
      name: {
        label: i18n.t('project_management.files.datacollection.header.name'),
      },
      createdOn: {
        label: i18n.t('project_management.files.datacollection.header.created_on'),
      },
    },
    defaultSorting: {
      field: 'createdOn',
      order: 'desc',
    },
  })

  return (
    <>
      <OneDataCollection
        source={source}
        onBulkAction={(action, selectedItems) => {
          const selectedItemIds = selectedItems.itemsStatus
            .filter((status) => status.checked)
            .map((status) => status.item.id)
          setSelectedItems(selectedItemIds)

          switch (action) {
            case 'delete-documents':
              handleOnClickDelete
              break
          }
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: compact([
                {
                  label: i18n.t('project_management.files.datacollection.header.name'),
                  render: (item: ProjectDocumentsDataCollectionItemFragment) => ({
                    type: 'text',
                    value: item.name,
                  }),
                  sorting: 'name',
                },
                {
                  label: i18n.t('project_management.files.datacollection.header.uploaded_by'),
                  render: (item: ProjectDocumentsDataCollectionItemFragment) => ({
                    type: 'person',
                    value: {
                      firstName: item.uploader?.firstName ?? '',
                      lastName: item.uploader?.lastName ?? '',
                      src: buildUrl(item.uploader?.access?.avatar),
                    },
                  }),
                },
                {
                  label: i18n.t('project_management.files.datacollection.header.created_on'),
                  render: (item: ProjectDocumentsDataCollectionItemFragment) => ({
                    type: 'date',
                    value: new Date(item.createdOn),
                  }),
                  sorting: 'createdOn',
                },
              ]),
            },
          },
        ]}
      />
      {isOpenModal && (
        <DeleteDocumentsModal
          onCloseModal={handleOnCloseModal}
          ids={selectedItems}
          projectId={projectId}
        />
      )}
    </>
  )
}

export default ProjectDocumentsDataCollection
